import { isValidVatNumber, normalizeVatNumber } from '@/utils/vatUtils'
import type { BillRequest, BillLineRequest, CustomBillLineRequest } from '@/model/Bill'
import type { Config } from '@/model/Company'
import type Customer from '@/model/Customer'
import type { CustomerRequest } from '@/model/Customer'
import {
  isNonCarrierRole,
  type DeliveryNoteRequest,
  type Payload,
  type PayloadDto,
  type TripDto
} from '@/model/DeliveryNote'
import { DeliveryNoteCustomFieldType, type DeliveryNoteCustomField } from '@/model/DeliveryNoteConfig'
import type { EmployeeRequest } from '@/model/Employee'
import type { ExpenseLineRequest, ExpenseRequest } from '@/model/Expense'
import type {
  Payload as LoadOrderPayload,
  PayloadDto as LoadOrderPayloadDto,
  LoadOrderRequest
} from '@/model/LoadOrder'
import type { NimaAuthorization, NimaRequest } from '@/model/Nima'
import type { PayloadRequest } from '@/model/Payload'
import type { PlaceRequest } from '@/model/Place'
import type { PriceRequest } from '@/model/Price'
import type { RefuelRequest } from '@/model/Refuel'

import type { DriverRequest } from '@/model/Driver'
import type {
  PassengerTransportDocument,
  PassengerTransportDocumentRequest
} from '@/model/PassengerTransportDocument'
import { isPassengerTransportDocumentV2 } from '@/model/PassengerTransportDocument'
import { hasValidPassengerTransportDeliveryRoute } from '@/utils/PassengerTransportDocumentMapping'
import type { VehicleRequest } from '@/model/Vehicle'
import moment from 'moment'

function isCreateCustomerValid(customer: CustomerRequest): boolean {
  return (
    isStringValid(customer.name) &&
    (!customer.vatNumber || isVatNumberValid(customer.vatNumber)) &&
    (!customer.email || isEmailValid(customer.email)) &&
    (!customer.postCode || isPostCodeValid(customer.postCode)) &&
    (!customer.phoneNumber || isPhoneNumberValid(customer.phoneNumber))
  )
}

function isCustomerCompleteForBilling(customer: Customer): boolean {
  return (
    isStringValid(customer.name) &&
    isStringValid(customer.businessName) &&
    isStringValid(customer.address) &&
    isStringValid(customer.postCode) &&
    isStringValid(customer.city) &&
    isStringValid(customer.state) &&
    isStringValid(customer.country) &&
    isStringValid(customer.vatNumber)
  )
}

function isCreateVehicleValid(vehicle: VehicleRequest): boolean {
  return isStringValid(vehicle.plate)
}

function isCreatePlaceValid(place: PlaceRequest): boolean {
  return isStringValid(place.name)
}

function isCreatePayloadValid(payload: PayloadRequest): boolean {
  return isStringValid(payload.name)
}

function isCreatePriceValid(price: PriceRequest): boolean {
  return (
    isStringValid(price.customerId) &&
    isStringValid(price.payload) &&
    (isStringValid(price.originPlaceId) || isStringValid(price.originName)) &&
    (isStringValid(price.destinationPlaceId) || isStringValid(price.destinationName)) &&
    isEnumValid(price.unit) &&
    isEnumValid(price.vatType) &&
    price.priceRanges &&
    price.priceRanges.length > 0 &&
    price.priceRanges.every(range =>
      isNumberValid(range.amount) &&
      isNumberValid(range.vat)
    )
  )
}

function isEmployeeExternalCompanyValid(
  associateExternal: boolean,
  externalCustomerId?: string
): boolean {
  return !associateExternal || isStringValid(externalCustomerId)
}

function isCreateEmployeeValid(
  employee: EmployeeRequest,
  associateExternal: boolean = false
): boolean {
  return (
    isStringValid(employee.name) &&
    isStringValid(employee.surname) &&
    isStringValid(employee.email) &&
    isStringValid(employee.phoneNumber) &&
    isEmployeeExternalCompanyValid(associateExternal, employee.externalCustomerId)
  )
}

type RegisterCompanyCompanyFields = {
  businessName: string
  name: string
  address: string
  postCode: string
  city: string
  state: string
  country: string
  email: string
  vatNumber: string
}

type RegisterCompanyEmployeeFields = {
  name: string
  surname: string
  email: string
  phoneNumber: string
  nif: string
}

function isRegisterCompanyValid(
  company: RegisterCompanyCompanyFields,
  employee: RegisterCompanyEmployeeFields,
  acceptTerms: boolean
): boolean {
  return (
    isStringValid(company.businessName) &&
    isStringValid(company.name) &&
    isStringValid(company.address) &&
    isStringValid(company.postCode) &&
    isPostCodeValid(company.postCode) &&
    isStringValid(company.city) &&
    isStringValid(company.state) &&
    isStringValid(company.country) &&
    isStringValid(company.email) &&
    isEmailValid(company.email) &&
    isStringValid(company.vatNumber) &&
    isVatNumberValid(normalizeVatNumber(company.vatNumber)) &&
    isStringValid(employee.name) &&
    isStringValid(employee.surname) &&
    isStringValid(employee.email) &&
    isEmailValid(employee.email) &&
    isStringValid(employee.phoneNumber) &&
    isPhoneNumberValid(employee.phoneNumber) &&
    isStringValid(employee.nif) &&
    acceptTerms
  )
}

function isCreateDriverValid(driver: DriverRequest): boolean {
  return (
    isStringValid(driver.name) &&
    isStringValid(driver.surname) &&
    (!driver.email || isEmailValid(driver.email)) &&
    (!driver.phoneNumber || isPhoneNumberValid(driver.phoneNumber))
  )
}

function isLoadOrderPayloadValid(payload: LoadOrderPayload | LoadOrderPayloadDto): boolean {
  return (
    isStringValid(payload.payload) &&
    (payload.unit === undefined || payload.unit === null || isEnumValid(payload.unit)) &&
    isOptionalPositiveNumberValid(payload.quantity, 0)
  )
}

function isLoadOrderPayloadsValid(payloads?: (LoadOrderPayload | LoadOrderPayloadDto)[]): boolean {
  const filled = (payloads ?? []).filter((p) => isStringValid(p.payload))
  return filled.every(isLoadOrderPayloadValid)
}

function isCreateLoadOrderValid(loadOrder: LoadOrderRequest): boolean {
  return (
    (loadOrder.autogenerateNumber === true || isStringValid(loadOrder.number)) &&
    isLoadOrderPayloadsValid(loadOrder.payloads)
  )
}

function isCreateDeliveryNoteValid(
  deliveryNote: DeliveryNoteRequest,
  customerToBillRequired: boolean = false,
  isLinkedDeliveryNote: boolean = false,
  skipRouteFields: boolean = false
): boolean {
  const hasVehicles =
    isArrayValid(deliveryNote.vehicleIds) ||
    (deliveryNote.vehiclePlates?.some((plate) => isStringValid(plate)) ?? false)

  const hasRoute =
    skipRouteFields ||
    ((isStringValid(deliveryNote.originId) || isStringValid(deliveryNote.originName)) &&
      (isStringValid(deliveryNote.destinationId) || isStringValid(deliveryNote.destinationName)))

  return (
    (deliveryNote.autogenerateNumber || isStringValid(deliveryNote.number)) &&
    // isDateValid(deliveryNote.date) &&
    hasRoute &&
    (isStringValid(deliveryNote.customerId) || isStringValid(deliveryNote.customerName)) &&
    (!customerToBillRequired || isStringValid(deliveryNote.customerToBillId)) &&
    // isStringValid(deliveryNote.payload) &&
    // isNumberValid(deliveryNote.quantity) &&
    // isEnumValid(deliveryNote.unit) &&
    (isLinkedDeliveryNote || hasVehicles)
  )
}

function isCreatePassengerTransportDocumentValid(
  document: PassengerTransportDocumentRequest,
  isEditing = false,
  existingDocument?: PassengerTransportDocument
): boolean {
  const operator = isNonCarrierRole(document.operatorRole)
  const useV2Route = isEditing
    ? isPassengerTransportDocumentV2(existingDocument ?? { schemaVersion: 1 })
    : true
  const routeValid = useV2Route
    ? hasValidPassengerTransportDeliveryRoute(document.deliveries)
    : (isStringValid(document.originId) || isStringValid(document.originName)) &&
      (isStringValid(document.destinationId) || isStringValid(document.destinationName))
  const hasListVehicles = isArrayValid(document.vehicleIds)
  const hasPlates = document.vehiclePlates?.some((plate) => isStringValid(plate)) ?? false
  const hasVehicles = hasListVehicles || (operator && hasPlates)
  const hasDriver =
    isStringValid(document.driverId) ||
    (isStringValid(document.driverName) &&
      isStringValid(document.driverSurname) &&
      isStringValid(document.driverNif))
  const hasCarrier =
    isStringValid(document.carrierId) ||
    (isStringValid(document.carrierName) && isStringValid(document.carrierVatNumber))

  return (
    (document.autogenerateNumber || isStringValid(document.number)) &&
    isStringValid(document.type) &&
    routeValid &&
    (isStringValid(document.customerId) || isStringValid(document.customerName)) &&
    hasVehicles &&
    (operator ? hasDriver && hasCarrier : isEditing || isStringValid(document.employeeId))
  )
}

function isPayloadsValid(payloads: Payload[] | PayloadDto[]) {
  return (
    payloads.length > 0 &&
    payloads.filter((payload) => isPayloadValid(payload)).length === payloads.length
  )
}

function isPayloadValid(payload: Payload | PayloadDto) {
  if (!isOptionalPositiveNumberValid(payload.quantity, 0)) {
    return false
  }
  if (isStringValid(payload.payloadId)) {
    return true
  }
  return (
    isStringValid(payload.payload) &&
    payload.unit !== undefined &&
    payload.unit !== null &&
    isEnumValid(payload.unit)
  )
}

function isTripsDtoValid(trips: TripDto[]) {
  return (
    trips.length === 0 ||
    (trips.length > 0 && trips.filter((trip) => isTripValid(trip)).length === trips.length)
  )
}

function isTripValid(trip: TripDto) {
  return isDateValid(trip.dateTime?.toISOString()) && isNumberValid(trip.amount)
}

function isCreateBillValid(bill: BillRequest): boolean {
  return (bill.autogenerateNumber || isStringValid(bill.number)) && isDateValid(bill.date)
}

function hasBillLines(
  lines?: BillLineRequest[],
  customLines?: CustomBillLineRequest[]
): boolean {
  return (lines?.length ?? 0) + (customLines?.length ?? 0) > 0
}

function isCreateCompanyConfigValid(config: Config): boolean {
  // TODO: Implement actual company config validation
  return true
}

function isCreateNimaValid(nima: NimaRequest): boolean {
  return isStringValid(nima.number)
}

function isNimaAuthorizationsValid(authorizations: NimaAuthorization[]): boolean {
  return (
    authorizations.length == 0 ||
    authorizations.filter((a) => isNimaAuthorizationValid(a)).length === authorizations.length
  )
}

function isNimaAuthorizationValid(authorization: NimaAuthorization): boolean {
  return isStringValid(authorization.number) && isStringValid(authorization.code)
}

function isCreateRefuelValid(refuel: RefuelRequest): boolean {
  return (
    isStringValid(refuel.vehicleId) &&
    isNumberValid(refuel.kilometres) &&
    isNumberValid(refuel.quantity)
  )
}

function isCreateExpenseValid(expense: ExpenseRequest): boolean {
  return isStringValid(expense.number) && isStringValid(expense.supplierId)
}

function isExpenseLinesValid(expenseLines: ExpenseLineRequest[]) {
  return (
    expenseLines.length > 0 &&
    expenseLines.filter((line) => isExpenseLineValid(line)).length === expenseLines.length
  )
}

function isExpenseLineValid(expenseLine: ExpenseLineRequest) {
  return (
    isStringValid(expenseLine.description) &&
    isPositiveNumberValid(expenseLine.quantity) &&
    isPositiveNumberValid(expenseLine.amount) &&
    isPositiveNumberValid(expenseLine.vat) &&
    isPositiveNumberValid(expenseLine.totalAmount)
  )
}

/* Internal validation */

function isStringValid(str?: string): boolean {
  return str !== undefined && str !== null && str.trim() !== ''
}

function isEmailValid(email?: string): boolean {
  if (!isStringValid(email)) return false
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email!)
}

function isPhoneNumberValid(phone?: string): boolean {
  if (!phone || phone.trim() === '') return true // Optional field
  // Accepts formats like: +34 123 456 789, 123456789, +34-123-456-789, etc.
  const phoneRegex = /^[\d\s\-\+\(\)]+$/
  return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 9
}

function isVatNumberValid(vat?: string): boolean {
  return isValidVatNumber(vat)
}

function isPostCodeValid(postCode?: string): boolean {
  if (!isStringValid(postCode)) return false
  // Post code: alphanumeric with optional spaces/hyphens, typically 4-10 chars
  const postCodeRegex = /^[A-Z0-9\s\-]{3,10}$/i
  return postCodeRegex.test(postCode!)
}

function isNumberValid(n: any | undefined, min?: number, max?: number): boolean {
  return (
    n !== '' &&
    n !== undefined &&
    n !== null &&
    (min == undefined || min <= n) &&
    (max == undefined || max >= n) &&
    !isNaN(+n)
  )
}

function isPositiveNumberValid(n: any | undefined, min?: number, max?: number): boolean {
  return (
    isNumberValid(n, 0) &&
    n !== '' &&
    n !== undefined &&
    n !== null &&
    (min == undefined || min <= n) &&
    (max == undefined || max >= n) &&
    !isNaN(+n)
  )
}

function isOptionalPositiveNumberValid(n: any | undefined, min?: number, max?: number): boolean {
  if (n === undefined || n === null) {
    return true
  }

  return isPositiveNumberValid(n, min, max)
}

function isEnumValid<T>(str: T): boolean {
  return str !== undefined && str !== null
}

function isDateValid(date: string): boolean {
  return date !== undefined && date !== null && moment(date).isValid()
}

function isArrayValid<T>(array?: T[] | null): boolean {
  return array !== undefined && array !== null && array.length > 0
}

const MAX_DELIVERY_NOTE_CUSTOM_FIELDS = 20

function isDeliveryNoteCustomFieldsConfigValid(customFields: DeliveryNoteCustomField[]): boolean {
  if (customFields.length > MAX_DELIVERY_NOTE_CUSTOM_FIELDS) {
    return false
  }

  const names = new Set<string>()
  for (const field of customFields) {
    const name = field.name?.trim() ?? ''
    if (!name || names.has(name)) {
      return false
    }
    names.add(name)

    if (field.type === DeliveryNoteCustomFieldType.OPTIONS) {
      if (!field.options?.length) {
        return false
      }
      const optionNames = new Set<string>()
      for (const option of field.options) {
        const optionName = option.name?.trim() ?? ''
        if (!optionName || optionNames.has(optionName)) {
          return false
        }
        optionNames.add(optionName)
      }
    }
  }

  return true
}

function isCustomFieldValueValid(field: DeliveryNoteCustomField, value: string): boolean {
  const normalizedValue = value.trim()
  if (!normalizedValue) {
    return true
  }

  if (field.type === DeliveryNoteCustomFieldType.NUMBER) {
    const parsed = Number(normalizedValue)
    return Number.isFinite(parsed)
  }

  if (field.type === DeliveryNoteCustomFieldType.OPTIONS) {
    const optionNames = new Set(
      (field.options ?? [])
        .map((option) => option.name?.trim())
        .filter((name): name is string => !!name)
    )
    return optionNames.has(normalizedValue)
  }

  return true
}

function getCustomFieldInvalidById(
  definitions: DeliveryNoteCustomField[],
  valuesById: Record<string, string>
): Record<string, boolean> {
  const invalidById: Record<string, boolean> = {}

  for (const field of definitions) {
    if (!field.id) {
      continue
    }

    const value = valuesById[field.id] ?? ''
    if (field.required && !value.trim()) {
      invalidById[field.id] = true
      continue
    }

    if (!isCustomFieldValueValid(field, value)) {
      invalidById[field.id] = true
    }
  }

  return invalidById
}

function isCustomFieldsValid(
  definitions: DeliveryNoteCustomField[],
  valuesById: Record<string, string>
): boolean {
  return Object.keys(getCustomFieldInvalidById(definitions, valuesById)).length === 0
}

export {
  getCustomFieldInvalidById, hasBillLines, isCreateBillValid,
  isCreateCompanyConfigValid,
  isCreateCustomerValid, isCreateDeliveryNoteValid, isCreateDriverValid, isCreateEmployeeValid, isCreateExpenseValid, isCreateLoadOrderValid, isCreateNimaValid,
  isCreatePassengerTransportDocumentValid,
  isCreatePayloadValid,
  isCreatePlaceValid,
  isCreatePriceValid,
  isCreateRefuelValid,
  isCreateVehicleValid, isCustomerCompleteForBilling, isCustomFieldsValid, isCustomFieldValueValid, isDeliveryNoteCustomFieldsConfigValid, isEmailValid, isEmployeeExternalCompanyValid, isExpenseLinesValid, isLoadOrderPayloadsValid, isLoadOrderPayloadValid, isNimaAuthorizationsValid,
  isNumberValid, isOptionalPositiveNumberValid, isPayloadValid, isPayloadsValid,
  isPhoneNumberValid,
  isPositiveNumberValid, isPostCodeValid,
  isRegisterCompanyValid,
  isStringValid,
  isTripsDtoValid,
  isVatNumberValid
}

